import { confirmInApp } from "../common/AppDialog";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useEditLock } from "../../context/EditLockContext";
import {
  toSortedArray,
  reorderTexts,
  reorderNotebooks,
  moveText,
  moveNotebook,
  moveNotebooksBatch,
  moveTextsBatch,
  updateText,
  deleteText,
  renameDesk,
  renameDrawer,
  renameNotebook,
  CATEGORY_PATHS,
} from "../../services/workspaceService";
import {
  useDeskDnDSensors,
  nbSortId,
  txtSortId,
  parseSortId,
  DROP_DRAWER_PREFIX,
  DROP_NB_PREFIX,
} from "../../hooks/useDeskDnDSensors";
import { navigateToText } from "../../utils/textRoutes";
import { rememberWritingText } from "../../utils/writingSession";
import Usable from "../common/Usable";
import { useNavigate, useParams } from "react-router-dom";
import {
  Layout as LayoutIcon,
  Briefcase,
  Layers,
  Book,
  ChevronRight,
  Plus,
  CheckSquare,
  Square,
  Trash2,
  GripVertical,
  Check,
  X,
} from "lucide-react";
import BackgroundImage from "../common/BackgroundImage";
import { BACKGROUNDS } from "../../config/backgrounds";
import deskGlass from "../../styles/deskGlass";

function DropChip({ id, label, sub, active }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`corkboard-drop-chip${isOver || active ? " corkboard-drop-chip--over" : ""}`}
    >
      <span className="corkboard-drop-chip__label">{label}</span>
      {sub != null && <span className="corkboard-drop-chip__sub">{sub}</span>}
    </div>
  );
}

function SortableNotebookCard({
  notebook,
  deskName,
  drawerName,
  locked,
  selectMode,
  selected,
  onToggleSelect,
  onOpen,
  onRename,
}) {
  const sortId = nbSortId(notebook.id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortId,
    disabled: locked || selectMode,
  });
  const textCount = Object.keys(notebook.texts || {}).length;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={`corkboard-card-wrap${isDragging ? " corkboard-card-wrap--ghost" : ""}`}
    >
      <Usable
        location={[deskName, drawerName, notebook.name]}
        title={notebook.name}
        onRename={onRename}
        onClick={selectMode ? onToggleSelect : onOpen}
        bodyStyle={{ display: "block", flex: 1 }}
        style={{
          ...styles.glassCard,
          borderTop: `4px solid ${notebook.color || "#D4AF37"}`,
          cursor: selectMode ? "pointer" : "default",
        }}
      >
        {selectMode && (
          <div
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              width: 22,
              height: 22,
              borderRadius: "50%",
              border: selected ? "none" : "2px solid rgba(255,255,255,0.4)",
              background: selected ? "#4ade80" : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {selected && <Check size={14} color="#000" />}
          </div>
        )}
        {!locked && !selectMode && (
          <button
            type="button"
            className="corkboard-grip"
            aria-label="Arrastrar cuaderno"
            {...attributes}
            {...listeners}
          >
            <GripVertical size={16} />
          </button>
        )}
        <div style={{ fontSize: "1rem", fontWeight: 700, color: "#fff", paddingRight: selectMode ? 28 : 0 }}>
          {notebook.name}
        </div>
        <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)", marginTop: 8, textTransform: "uppercase" }}>
          {textCount} relatos
        </div>
      </Usable>
    </div>
  );
}

function SortableTextRow({
  text,
  deskName,
  drawerName,
  notebookName,
  locked,
  selectMode,
  selected,
  onToggleSelect,
  onOpen,
  onRename,
  onToggleTask,
  onDelete,
}) {
  const sortId = txtSortId(text.id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortId,
    disabled: locked || selectMode,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`corkboard-row-wrap${isDragging ? " corkboard-row-wrap--ghost" : ""}`}
    >
      <Usable
        dragHandleProps={locked || selectMode ? undefined : { ...attributes, ...listeners }}
        location={[deskName, drawerName, notebookName, text.title]}
        title={text.title}
        onRename={onRename}
        copyLabel="Copiar título"
        copyValue={text.title}
        onClick={selectMode ? onToggleSelect : onOpen}
        style={{
          ...styles.glassListItem,
          ...(selected && selectMode ? { borderColor: "rgba(212,175,55,0.5)" } : {}),
        }}
      >
        {selectMode && (
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              border: selected ? "none" : "2px solid rgba(255,255,255,0.4)",
              background: selected ? "#4ade80" : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {selected && <Check size={14} color="#000" />}
          </div>
        )}
        <Book size={18} color="rgba(255,255,255,0.4)" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {text.title}
          </div>
          <div style={{ fontSize: "0.7rem", opacity: 0.3 }}>{new Date(text.updatedAt).toLocaleDateString()}</div>
        </div>
        {!selectMode && (
          <div style={{ display: "flex", gap: 4 }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={onToggleTask}
              disabled={locked}
              style={{ ...styles.taskBtn, ...(locked ? { opacity: 0.35, cursor: "not-allowed" } : {}) }}
              title="Convertir en Tarea"
            >
              {(text.content || "").includes("[ ]") || (text.content || "").includes("- [ ]") ? (
                <CheckSquare size={18} color="#4ade80" />
              ) : (
                <Square size={18} color="rgba(255,255,255,0.2)" />
              )}
            </button>
            <button
              type="button"
              onClick={(e) => {
                console.log("[DrawersRoom] delete click", { textId: text.id, title: text.title, locked });
                onDelete(e);
              }}
              disabled={locked}
              style={{ ...styles.taskBtn, color: "rgba(255,255,255,0.2)", ...(locked ? { opacity: 0.35, cursor: "not-allowed" } : {}) }}
              title="Eliminar"
            >
              <Trash2 size={18} />
            </button>
          </div>
        )}
        <ChevronRight size={18} opacity={0.2} />
      </Usable>
    </div>
  );
}

export default function DrawersRoom() {
  const { workspace, loading: workspaceLoading, loadData } = useWorkspace();
  const { canEdit } = useEditLock();
  const locked = !canEdit;
  const navigate = useNavigate();
  const { deskSlug, drawerSlug, notebookSlug } = useParams();
  const skipUrlPushRef = useRef(!!deskSlug);

  const [selectedDeskId, setSelectedDeskId] = useState("");
  const [selectedDrawerId, setSelectedDrawerId] = useState("");
  const [selectedNotebookId, setSelectedNotebookId] = useState("");
  const [activeLevel, setActiveLevel] = useState(0);
  const [undoAction, setUndoAction] = useState(null);
  const [activeDrag, setActiveDrag] = useState(null);
  const [selectMode, setSelectMode] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [batchMoveOpen, setBatchMoveOpen] = useState(false);
  const swiperRef = useRef(null);
  const sensors = useDeskDnDSensors(locked);

  const desks = useMemo(() => {
    if (!workspace?.desks) return [];
    return Array.isArray(workspace.desks) ? workspace.desks : toSortedArray(workspace.desks);
  }, [workspace]);

  const temasDesk = useMemo(
    () => desks.find((d) => d.name === "Temas") || null,
    [desks]
  );
  const mainCategories = useMemo(() => {
    if (!temasDesk) return Object.values(CATEGORY_PATHS).map((p) => ({ name: p.drawerName, drawerId: null }));
    const drawersMap = temasDesk.drawers || {};
    return Object.values(CATEGORY_PATHS).map((p) => {
      const entry = Object.entries(drawersMap).find(([, d]) => d.name === p.drawerName);
      return { name: p.drawerName, drawerId: entry?.[0] || null };
    });
  }, [temasDesk]);

  const selectedDesk = desks.find((d) => d.id === selectedDeskId) || desks[0];
  const drawers = useMemo(() => toSortedArray(selectedDesk?.drawers) || [], [selectedDesk]);
  const selectedDrawer = drawers.find((d) => d.id === selectedDrawerId) || drawers[0];
  const notebooks = useMemo(() => toSortedArray(selectedDrawer?.notebooks) || [], [selectedDrawer]);
  const selectedNotebook = notebooks.find((n) => n.id === selectedNotebookId) || notebooks[0];
  const texts = useMemo(() => toSortedArray(selectedNotebook?.texts) || [], [selectedNotebook]);

  const notebookSortIds = useMemo(() => notebooks.map((n) => nbSortId(n.id)), [notebooks]);
  const textSortIds = useMemo(() => texts.map((t) => txtSortId(t.id)), [texts]);

  useEffect(() => {
    if (!workspace?.desks?.length) return;
    if (deskSlug) {
      skipUrlPushRef.current = true;
      const desk = desks.find((d) => d.slug === deskSlug);
      if (desk) {
        setSelectedDeskId(desk.id);
        const drs = toSortedArray(desk.drawers);
        if (drawerSlug) {
          const dr = drs.find((d) => d.slug === drawerSlug);
          if (dr) {
            setSelectedDrawerId(dr.id);
            const nbs = toSortedArray(dr.notebooks);
            if (notebookSlug) {
              const nb = nbs.find((n) => n.slug === notebookSlug);
              if (nb) setSelectedNotebookId(nb.id);
            }
          }
        }
      }
      requestAnimationFrame(() => {
        skipUrlPushRef.current = false;
      });
      return;
    }
    if (!selectedDeskId && desks[0]) setSelectedDeskId(desks[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace, deskSlug, drawerSlug, notebookSlug]);

  useEffect(() => {
    if (!selectedDeskId && desks[0] && !deskSlug) setSelectedDeskId(desks[0].id);
  }, [desks, selectedDeskId, deskSlug]);

  useEffect(() => {
    if (selectedDeskId && !selectedDrawerId && drawers[0] && !drawerSlug) setSelectedDrawerId(drawers[0].id);
  }, [drawers, selectedDeskId, selectedDrawerId, drawerSlug]);

  useEffect(() => {
    if (selectedDrawerId && !selectedNotebookId && notebooks[0] && !notebookSlug) setSelectedNotebookId(notebooks[0].id);
  }, [notebooks, selectedDrawerId, selectedNotebookId, notebookSlug]);

  useEffect(() => {
    if (skipUrlPushRef.current || !selectedDesk?.slug) return;
    let path = `/drawers/${selectedDesk.slug}`;
    if (selectedDrawer?.slug) path += `/${selectedDrawer.slug}`;
    if (selectedNotebook?.slug && selectedDrawer?.slug) path += `/${selectedNotebook.slug}`;
    const current = window.location.pathname.replace(/\/$/, "");
    if (current !== path) navigate(path, { replace: true });
  }, [selectedDesk?.slug, selectedDrawer?.slug, selectedNotebook?.slug, navigate, selectedDesk, selectedDrawer, selectedNotebook]);

  const scrollToLevel = (level) => {
    const clamped = Math.max(0, Math.min(3, level));
    setActiveLevel(clamped);
    if (swiperRef.current?.offsetWidth > 0) {
      swiperRef.current.scrollTo({ left: clamped * swiperRef.current.offsetWidth, behavior: "smooth" });
    }
  };

  const openTemasCategory = useCallback(
    (drawerId) => {
      if (!temasDesk) return;
      setSelectedDeskId(temasDesk.id);
      setSelectedDrawerId(drawerId || "");
      setSelectedNotebookId("");
      scrollToLevel(drawerId ? 2 : 1);
    },
    [temasDesk]
  );

  const exitSelectMode = useCallback(() => {
    setSelectMode(null);
    setSelectedIds(new Set());
    setBatchMoveOpen(false);
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openReader = (text) => {
    if (!text) return;
    const enriched = {
      ...text,
      deskId: selectedDeskId,
      drawerId: selectedDrawerId,
      notebookId: selectedNotebookId,
      deskSlug: selectedDesk?.slug,
      drawerSlug: selectedDrawer?.slug,
      notebookSlug: selectedNotebook?.slug,
    };
    rememberWritingText(enriched);
    navigateToText(navigate, enriched);
  };

  const handleDeleteText = async (e, t) => {
    e.stopPropagation();
    console.log("[DrawersRoom] handleDeleteText", {
      textId: t.id,
      title: t.title,
      locked,
      selectedDeskId,
      selectedDrawerId,
      selectedNotebookId,
    });
    if (locked) {
      console.warn("[DrawersRoom] delete blocked — edit lock (tap logo 5× to unlock)");
      return;
    }
    if (!(await confirmInApp({
      title: "Eliminar texto",
      message: `¿Eliminar "${t.title}"?`,
      confirmLabel: "Eliminar",
      danger: true,
    }))) {
      console.log("[DrawersRoom] delete cancelled by user", { textId: t.id });
      return;
    }
    try {
      await deleteText(selectedDeskId, selectedDrawerId, selectedNotebookId, t.id);
      console.log("[DrawersRoom] delete ok", { textId: t.id });
      if (loadData) await loadData();
    } catch (err) {
      console.error("[DrawersRoom] delete failed", { textId: t.id, error: err });
    }
  };

  const triggerUndo = (type, data) => {
    setUndoAction({ type, data });
    setTimeout(() => setUndoAction(null), 5000);
  };

  const handleUndo = async () => {
    if (locked || !undoAction) return;
    const { type, data } = undoAction;
    if (type === "task-toggle") {
      await updateText(selectedDeskId, selectedDrawerId, selectedNotebookId, data.id, { content: data.prevContent });
      if (loadData) await loadData();
    }
    setUndoAction(null);
  };

  const handleToggleTask = async (e, t) => {
    e.stopPropagation();
    if (locked) return;
    const hasTask = (t.content || "").includes("[ ]") || (t.content || "").includes("- [ ]");
    let newContent = t.content || "";
    const prevContent = t.content;
    if (hasTask) {
      newContent = newContent.replace(/\[\s*\]/g, "").replace(/-\s*\[\s*\]/g, "").trim();
    } else {
      newContent = "[ ] " + newContent;
    }
    await updateText(selectedDeskId, selectedDrawerId, selectedNotebookId, t.id, { content: newContent });
    if (loadData) await loadData();
    triggerUndo("task-toggle", { id: t.id, prevContent });
  };

  const handleBatchMove = async (destDrawerId) => {
    if (locked || !selectedIds.size) return;
    try {
      if (selectMode === "notebooks") {
        await moveNotebooksBatch(selectedDeskId, selectedDrawerId, [...selectedIds], selectedDeskId, destDrawerId);
      } else if (selectMode === "texts") {
        await moveTextsBatch(
          selectedDeskId,
          selectedDrawerId,
          selectedNotebookId,
          [...selectedIds],
          selectedDeskId,
          selectedDrawerId,
          destDrawerId
        );
      }
      exitSelectMode();
      if (loadData) await loadData();
    } catch (err) {
      console.error("[Drawers] batch move failed:", err);
    }
  };

  const handleDragStart = (event) => {
    const parsed = parseSortId(event.active.id);
    if (!parsed) return;
    const item =
      parsed.type === "notebook"
        ? notebooks.find((n) => n.id === parsed.id)
        : texts.find((t) => t.id === parsed.id);
    setActiveDrag({ ...parsed, item });
    if (swiperRef.current) swiperRef.current.style.overflowX = "hidden";
  };

  const handleDragCancel = () => {
    setActiveDrag(null);
    if (swiperRef.current) swiperRef.current.style.overflowX = "auto";
  };

  const handleDragEnd = async (event) => {
    setActiveDrag(null);
    if (swiperRef.current) swiperRef.current.style.overflowX = "auto";
    if (locked) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = parseSortId(active.id);
    const to = parseSortId(over.id);
    if (!from) return;

    try {
      if (from.type === "notebook") {
        if (to?.type === "drawer-drop" && to.id !== selectedDrawerId) {
          const destDrawer = drawers.find((d) => d.id === to.id);
          const destCount = destDrawer ? Object.keys(destDrawer.notebooks || {}).length : 0;
          await moveNotebook(selectedDeskId, selectedDrawerId, from.id, selectedDeskId, to.id, destCount);
        } else if (to?.type === "notebook") {
          const oldIndex = notebooks.findIndex((n) => n.id === from.id);
          const newIndex = notebooks.findIndex((n) => n.id === to.id);
          if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            const reordered = arrayMove(notebooks, oldIndex, newIndex);
            await reorderNotebooks(selectedDeskId, selectedDrawerId, reordered.map((n) => n.id));
          }
        }
      }

      if (from.type === "text") {
        if (to?.type === "notebook-drop" && to.id !== selectedNotebookId) {
          await moveText(
            selectedDeskId,
            selectedDrawerId,
            selectedNotebookId,
            from.id,
            selectedDeskId,
            selectedDrawerId,
            to.id
          );
        } else if (to?.type === "text") {
          const oldIndex = texts.findIndex((t) => t.id === from.id);
          const newIndex = texts.findIndex((t) => t.id === to.id);
          if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            const reordered = arrayMove(texts, oldIndex, newIndex);
            await reorderTexts(selectedDeskId, selectedDrawerId, selectedNotebookId, reordered.map((t) => t.id));
          }
        }
      }

      if (loadData) await loadData();
    } catch (err) {
      console.error("[DnD] Error:", err);
    }
  };

  const renderMoveBar = () => {
    if (!activeDrag || locked) return null;
    if (activeDrag.type === "notebook") {
      return (
        <div className="corkboard-move-bar" role="region" aria-label="Soltar en cajón">
          <span className="corkboard-move-bar__hint">Soltar en cajón →</span>
          <div className="corkboard-move-bar__chips">
            {drawers.map((d) => (
              <DropChip
                key={d.id}
                id={`${DROP_DRAWER_PREFIX}${d.id}`}
                label={d.name}
                sub={`${Object.keys(d.notebooks || {}).length}`}
                active={d.id === selectedDrawerId}
              />
            ))}
          </div>
        </div>
      );
    }
    if (activeDrag.type === "text") {
      return (
        <div className="corkboard-move-bar" role="region" aria-label="Soltar en cuaderno">
          <span className="corkboard-move-bar__hint">Soltar en cuaderno →</span>
          <div className="corkboard-move-bar__chips">
            {notebooks.map((n) => (
              <DropChip
                key={n.id}
                id={`${DROP_NB_PREFIX}${n.id}`}
                label={n.name}
                sub={`${Object.keys(n.texts || {}).length}`}
                active={n.id === selectedNotebookId}
              />
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderBatchBar = () => {
    if (!selectMode || !selectedIds.size) return null;
    return (
      <div className="corkboard-batch-bar">
        <span>{selectedIds.size} seleccionado{selectedIds.size > 1 ? "s" : ""}</span>
        <div style={{ position: "relative" }}>
          <button type="button" className="corkboard-batch-btn" onClick={() => setBatchMoveOpen((o) => !o)}>
            Mover a…
          </button>
          {batchMoveOpen && (
            <div className="corkboard-batch-menu">
              {(selectMode === "notebooks" ? drawers : notebooks).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="corkboard-batch-menu__item"
                  onClick={() => handleBatchMove(item.id)}
                >
                  {item.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="button" className="corkboard-batch-btn corkboard-batch-btn--ghost" onClick={exitSelectMode}>
          <X size={16} />
        </button>
      </div>
    );
  };

  if (workspaceLoading) return <div style={styles.center}>Cargando Archivo...</div>;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div style={styles.glassContainer}>
        <BackgroundImage image={BACKGROUNDS.dashboard} />

        {undoAction && (
          <div style={styles.undoToast}>
            <span style={{ fontSize: "0.9rem" }}>Relato modificado</span>
            <button type="button" onClick={handleUndo} style={styles.undoBtn}>
              DESHACER
            </button>
          </div>
        )}

        {renderBatchBar()}

        <div
          ref={swiperRef}
          style={{
            ...styles.swiper,
            scrollSnapType: activeDrag ? "none" : "x mandatory",
          }}
          onScroll={(e) => {
            const level = Math.round(e.target.scrollLeft / e.target.offsetWidth);
            if (level !== activeLevel) setActiveLevel(level);
          }}
        >
          {/* LEVEL 0: DESKS */}
          <div style={styles.slide}>
            <div style={styles.deskContent}>
              <div style={styles.listHeader}>Categorías</div>
              <p style={styles.hint}>
                Código · Iglesia · Vida · Cuentos viven en <strong style={{ color: "rgba(255,255,255,0.7)" }}>Temas</strong> (cajones). Tocá uno para entrar.
              </p>
              <div style={styles.categoryStrip}>
                {mainCategories.map((cat) => (
                  <button
                    key={cat.name}
                    type="button"
                    disabled={!temasDesk || !cat.drawerId}
                    onClick={() => openTemasCategory(cat.drawerId)}
                    style={{
                      ...styles.categoryChip,
                      opacity: !temasDesk || !cat.drawerId ? 0.4 : 1,
                      cursor: !temasDesk || !cat.drawerId ? "wait" : "pointer",
                    }}
                    title={temasDesk ? `Temas › ${cat.name}` : "Creando Temas…"}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div style={{ ...styles.listHeader, marginTop: 28 }}>Escritorios</div>
              <p style={styles.hint}>
                ⋯ en la tarjeta → Renombrar. Arrastrar escritorios no está (son pesados). DnD: cuadernos y textos.
              </p>
              <div style={styles.grid}>
                {desks.map((d) => {
                  const drawerCount = Object.keys(d.drawers || {}).length;
                  const isTemas = d.name === "Temas";
                  return (
                  <Usable
                    key={d.id}
                    location={[d.name]}
                    title={d.name}
                    onRename={locked ? undefined : async (name) => { await renameDesk(d.id, name); if (loadData) await loadData(); }}
                    onClick={() => { setSelectedDeskId(d.id); setSelectedDrawerId(""); setSelectedNotebookId(""); scrollToLevel(1); }}
                    bodyStyle={{ display: "block", flex: 1 }}
                    style={{
                      ...styles.glassCard,
                      borderColor: selectedDeskId === d.id
                        ? "rgba(255,255,255,0.5)"
                        : isTemas
                          ? "rgba(212,175,55,0.45)"
                          : "rgba(255,255,255,0.1)",
                    }}
                  >
                    <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff" }}>{d.name}</div>
                    {isTemas && (
                      <div style={{ fontSize: "0.65rem", color: "#D4AF37", marginTop: 4, letterSpacing: "0.5px" }}>
                        CATEGORÍAS PRINCIPALES
                      </div>
                    )}
                    <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)", marginTop: 8, textTransform: "uppercase", letterSpacing: "1px" }}>
                      {drawerCount} {drawerCount === 1 ? "cajón" : "cajones"}
                    </div>
                  </Usable>
                  );
                })}
              </div>
            </div>
          </div>

          {/* LEVEL 1: DRAWERS — rename only, no reorder */}
          <div style={styles.slide}>
            <div style={styles.deskContent}>
              <div style={styles.listHeader}>Cajones de &quot;{selectedDesk?.name}&quot;</div>
              <p style={styles.hint}>Los cajones no se mueven — solo renombrar.</p>
              <div style={styles.list}>
                {drawers.map((d) => (
                  <Usable
                    key={d.id}
                    location={[selectedDesk?.name, d.name]}
                    title={d.name}
                    onRename={locked ? undefined : async (name) => { await renameDrawer(selectedDeskId, d.id, name); if (loadData) await loadData(); }}
                    onClick={() => { setSelectedDrawerId(d.id); setSelectedNotebookId(""); exitSelectMode(); scrollToLevel(2); }}
                    style={{ ...styles.glassListItem, background: selectedDrawerId === d.id ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.03)" }}
                  >
                    <Briefcase size={18} color="rgba(255,255,255,0.4)" />
                    <span style={{ flex: 1, fontWeight: 600 }}>{d.name}</span>
                    <span style={{ fontSize: "0.7rem", opacity: 0.5 }}>{Object.keys(d.notebooks || {}).length} cuadernos</span>
                    <ChevronRight size={18} opacity={0.2} />
                  </Usable>
                ))}
              </div>
            </div>
          </div>

          {/* LEVEL 2: NOTEBOOKS — sortable + cross-drawer drop bar */}
          <div style={styles.slide}>
            <div style={styles.deskContent}>
              <div style={styles.listHeaderRow}>
                <div style={styles.listHeaderInline}>Cuadernos en &quot;{selectedDrawer?.name}&quot;</div>
                {!locked && (
                  <button
                    type="button"
                    style={styles.selectToggle}
                    onClick={() => {
                      if (selectMode === "notebooks") exitSelectMode();
                      else { exitSelectMode(); setSelectMode("notebooks"); }
                    }}
                  >
                    {selectMode === "notebooks" ? "Listo" : "Seleccionar"}
                  </button>
                )}
              </div>
              {!locked && !selectMode && (
                <p style={styles.hint}>Arrastrá ⋮ para reordenar o mover a otro cajón.</p>
              )}
              <SortableContext items={notebookSortIds} strategy={rectSortingStrategy}>
                <div style={styles.grid}>
                  {notebooks.map((n) => (
                    <SortableNotebookCard
                      key={n.id}
                      notebook={n}
                      deskName={selectedDesk?.name}
                      drawerName={selectedDrawer?.name}
                      locked={locked}
                      selectMode={selectMode === "notebooks"}
                      selected={selectedIds.has(n.id)}
                      onToggleSelect={() => toggleSelect(n.id)}
                      onOpen={() => { setSelectedNotebookId(n.id); scrollToLevel(3); }}
                      onRename={locked ? undefined : async (name) => { await renameNotebook(selectedDeskId, selectedDrawerId, n.id, name); if (loadData) await loadData(); }}
                    />
                  ))}
                </div>
              </SortableContext>
            </div>
          </div>

          {/* LEVEL 3: TEXTS */}
          <div style={styles.slide}>
            <div style={styles.deskContent}>
              <div style={styles.listHeaderRow}>
                <div style={styles.listHeaderInline}>Contenido de &quot;{selectedNotebook?.name}&quot;</div>
                {!locked && (
                  <button
                    type="button"
                    style={styles.selectToggle}
                    onClick={() => {
                      if (selectMode === "texts") exitSelectMode();
                      else { exitSelectMode(); setSelectMode("texts"); }
                    }}
                  >
                    {selectMode === "texts" ? "Listo" : "Seleccionar"}
                  </button>
                )}
              </div>
              {!locked && !selectMode && (
                <p style={styles.hint}>Arrastrá ⋮⋮ para reordenar o soltar en otro cuaderno.</p>
              )}
              <SortableContext items={textSortIds} strategy={verticalListSortingStrategy}>
                <div style={styles.list}>
                  {texts.length === 0 ? (
                    <div style={styles.empty}>Este cuaderno está vacío.</div>
                  ) : (
                    texts.map((t) => (
                      <SortableTextRow
                        key={t.id}
                        text={t}
                        deskName={selectedDesk?.name}
                        drawerName={selectedDrawer?.name}
                        notebookName={selectedNotebook?.name}
                        locked={locked}
                        selectMode={selectMode === "texts"}
                        selected={selectedIds.has(t.id)}
                        onToggleSelect={() => toggleSelect(t.id)}
                        onOpen={() => openReader(t)}
                        onRename={locked ? undefined : async (name) => { await updateText(selectedDeskId, selectedDrawerId, selectedNotebookId, t.id, { title: name }); if (loadData) await loadData(); }}
                        onToggleTask={(e) => handleToggleTask(e, t)}
                        onDelete={(e) => handleDeleteText(e, t)}
                      />
                    ))
                  )}
                </div>
              </SortableContext>
              <button
                type="button"
                onClick={() => navigate("/editor", { state: { autoStart: false, deskId: selectedDeskId, drawerId: selectedDrawerId, notebookId: selectedNotebookId } })}
                disabled={locked}
                style={{ ...styles.addBtn, ...(locked ? { opacity: 0.35, cursor: "not-allowed", filter: "grayscale(1)" } : {}) }}
              >
                <Plus size={18} /> Nuevo Relato
              </button>
            </div>
          </div>
        </div>

        {renderMoveBar()}

        <DragOverlay dropAnimation={{ duration: 220, easing: "cubic-bezier(0.18, 0.67, 0.32, 1.15)" }}>
          {activeDrag?.item && (
            <div className="corkboard-drag-overlay">
              {activeDrag.type === "notebook" ? (
                <>
                  <div style={{ fontWeight: 700 }}>{activeDrag.item.name}</div>
                  <div style={{ fontSize: "0.7rem", opacity: 0.6 }}>cuaderno</div>
                </>
              ) : (
                <>
                  <div style={{ fontWeight: 600 }}>{activeDrag.item.title}</div>
                  <div style={{ fontSize: "0.7rem", opacity: 0.6 }}>relato</div>
                </>
              )}
            </div>
          )}
        </DragOverlay>

        <div style={styles.hierarchyNav}>
          {[
            { icon: <LayoutIcon size={18} />, label: "ESCRITORIOS", count: desks.length },
            { icon: <Briefcase size={18} />, label: "CAJONES", count: drawers.length },
            { icon: <Layers size={18} />, label: "CUADERNOS", count: notebooks.length },
            { icon: <Book size={18} />, label: "PÁGINAS", count: texts.length },
          ].map((item, i) => (
            <button
              key={item.label}
              type="button"
              onClick={() => scrollToLevel(i)}
              style={{
                ...styles.navTab,
                color: activeLevel === i ? "#fff" : "rgba(255,255,255,0.3)",
                borderTop: activeLevel === i ? "3px solid #fff" : "3px solid transparent",
              }}
            >
              {item.icon}
              <span style={styles.tabLabel}>{item.label} ({item.count})</span>
            </button>
          ))}
        </div>
      </div>
    </DndContext>
  );
}

const styles = {
  ...deskGlass,
  glassContainer: { ...deskGlass.container, background: "transparent" },
  center: { ...deskGlass.center, background: "#000" },
  deskContent: { ...deskGlass.deskContent, padding: "24px 20px 140px 20px" },
  glassCard: { ...deskGlass.glassCard, padding: "24px 20px", position: "relative" },
  categoryStrip: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "center",
    marginBottom: 8,
  },
  categoryChip: {
    ...deskGlass.viewToggle,
    fontSize: "0.8rem",
    padding: "8px 14px",
    borderColor: "rgba(212,175,55,0.35)",
    color: "#D4AF37",
  },
  glassListItem: {
    ...deskGlass.glassListItem,
    gap: "16px",
    padding: "18px 20px",
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.05)",
  },
  undoToast: deskGlass.undoToast,
  undoBtn: deskGlass.undoBtn,
  hint: { fontSize: "0.75rem", color: "rgba(255,255,255,0.35)", textAlign: "center", margin: "-12px 0 16px" },
  listHeaderRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 },
  listHeaderInline: {
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "3px",
    color: "rgba(255,255,255,0.3)",
    fontWeight: 900,
    flex: 1,
    textAlign: "center",
  },
  selectToggle: {
    ...deskGlass.viewToggle,
    fontSize: "0.7rem",
    padding: "6px 12px",
    flexShrink: 0,
  },
  hierarchyNav: {
    display: "flex",
    background: "rgba(20, 20, 20, 0.8)",
    backdropFilter: "blur(32px)",
    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
    height: "80px",
    flexShrink: 0,
    zIndex: 100,
    paddingBottom: "env(safe-area-inset-bottom)",
  },
  navTab: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s",
  },
  tabLabel: { fontSize: "0.5rem", textTransform: "uppercase", fontWeight: 800, letterSpacing: "1px" },
  swiper: {
    flex: 1,
    display: "flex",
    overflowX: "auto",
    scrollSnapType: "x mandatory",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
  },
  slide: {
    flex: "0 0 100%",
    width: "100%",
    height: "100%",
    scrollSnapAlign: "start",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },
  listHeader: {
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "3px",
    color: "rgba(255,255,255,0.3)",
    marginBottom: 24,
    fontWeight: 900,
    textAlign: "center",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: 16,
  },
  list: { display: "flex", flexDirection: "column", gap: 12 },
  empty: { textAlign: "center", padding: "60px 20px", color: "rgba(255,255,255,0.2)", fontStyle: "italic", fontSize: "0.9rem" },
  addBtn: {
    marginTop: 24,
    padding: 18,
    borderRadius: 20,
    border: "1px dashed rgba(255,255,255,0.2)",
    background: "rgba(255,255,255,0.02)",
    color: "rgba(255,255,255,0.5)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    fontWeight: 700,
    fontSize: "0.9rem",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  taskBtn: {
    background: "transparent",
    border: "none",
    padding: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};
